'use strict';

/**
 * azure-functions/shared/employees/competenceGapTask.cjs
 *
 * The work an unmet role competence requirement owes (RCQA-04,
 * rcq-role-competence-session-plan.md). A Gap / Not assessed / Expired chip on
 * the employee's Competence section asks nobody to do anything — monitoring is
 * not an event (TRYGGR_INTENT.md §12) — so every unmet requirement of a role
 * an employee currently holds carries ONE task in the one task system
 * (state.adhocTasks[], docs/task-system-redesign.md), linked back to the
 * employee, the role and the requirement. Same shape as the BC/DR re-test task
 * (shared/bcdr/retestTask.cjs).
 *
 * Pure: no I/O. The reconcile that reads and writes is competenceGapTaskStore.js.
 *
 *   One task per (employee, role, requirement, cycle). The id is
 *   deterministic — rc-gap-<employeeId>-<roleId>-<requirementId>-<cycle> —
 *   so a re-run, a retried write or a second writer finds the task already
 *   made (idempotent, §13). cycle = the expiry in ms for an Expired
 *   requirement (each lapse is its own re-assessment), else 'open'.
 *
 *   The task CLOSES only on evidence: a competenceAssessments[] record for
 *   that role and requirement, assessed AFTER the task was created, that makes
 *   the requirement Met (closedReason 'assessed-met', the assessment id kept
 *   on the task) and carries evidence; or, for a training requirement whose
 *   role says completion suffices (Agreed design 5), a training completion
 *   dated after the task (closedReason 'training-completed', co-owner Q2).
 *   A legacy roleScores value or any status change never closes it. The requirement leaving the employee —
 *   role unassigned, requirement removed from the role, employee departed or
 *   deleted — closes it with that reason instead (never "met"). A closed task
 *   whose requirement is unmet again in the same cycle is REOPENED.
 *
 *   No person closes it: state-write restores the stored task on any close,
 *   deletion, creation or link edit a payload carries (guardCompetenceGapTasks
 *   below), so the only route to "done" is recording the assessment.
 *
 *   Owner = the employee's line manager's linked user (the person allowed to
 *   assess, competenceAssessorBasis), else unassigned — then tenant admins see
 *   it in My action items (UNASSIGNED_ADMIN_TASK_TYPES).
 */

const { deriveCompetenceStatus, lineManagerUserId } = require('../roleCompetence.cjs');
const { withSystemClosure } = require('../tasks/closure.cjs');
const { isSampleEmployee, realEmployees } = require('../sampleRecords.cjs');

const TASK_TYPE = 'competence-gap';
const SOURCE_TYPE = 'role-competence';
const DUE_DAYS = 30;
const DAY_MS = 86400000;

/** Fields only the server writes on a competence-gap task. */
const SERVER_OWNED_FIELDS = Object.freeze([
  'type',
  'source',
  'sourceType',
  'sourceId',
  'createdAt',
  'createdBy',
  'linkedEmployeeId',
  'linkedRoleId',
  'linkedRequirementId',
  'gapReason',
  'gapStatus',
  'expiresAt',
  'cycle',
  'closedByAssessmentId',
  'reopenedAt',
  'reopenReason',
  'reopenings',
  'supersededBy',
  'closedAt',
  'closedBy',
  'closedReason',
]);

const UNMET = new Set(['gap', 'not-assessed', 'expired']);
/** A re-assessment task is opened this long before a Met assessment lapses,
 *  due on the expiry date, so the Check happens before the gap (co-owner Q4). */
const LEAD_DAYS = 30;

function hasEvidence(ev) {
  return !!ev && typeof ev === 'object' && ((typeof ev.artefactId === 'string' && ev.artefactId.trim()) || (typeof ev.note === 'string' && ev.note.trim()));
}

/** Owed now: unmet, or Met but lapsing within LEAD_DAYS. */
function isOwed(row, atMs) {
  if (UNMET.has(row.status)) return true;
  const exp = toMs(row.expiresAt);
  return row.status === 'met' && Number.isFinite(exp) && exp - LEAD_DAYS * DAY_MS <= atMs;
}

function toMs(v) {
  if (v == null || v === '') return NaN;
  const ms = v instanceof Date ? v.getTime() : Date.parse(v);
  return Number.isFinite(ms) ? ms : NaN;
}

function cycleOf(row) {
  if ((row.status === 'expired' || row.status === 'met') && Number.isFinite(toMs(row.expiresAt))) return String(toMs(row.expiresAt));
  return 'open';
}

function gapTaskId(employeeId, roleId, requirementId, cycle) {
  return `rc-gap-${employeeId}-${roleId}-${requirementId}-${cycle}`;
}

/** Roles the employee holds now (assignedRoles[] without an unassignedAt). */
function heldRoles(employee, roles) {
  const assigned = Array.isArray(employee?.assignedRoles) ? employee.assignedRoles : [];
  const out = [];
  for (const a of assigned) {
    if (!a || a.unassignedAt) continue;
    const role = roles.find((r) => r && r.id === a.roleDefId);
    if (role && !out.includes(role)) out.push(role);
  }
  return out;
}

function isActiveEmployee(e) {
  return e && e.id && e.status !== 'departed' && !isSampleEmployee(e);
}

function employeeLabel(e) {
  return e?.name || [e?.firstName, e?.lastName].filter(Boolean).join(' ') || e?.email || e?.id || 'employee';
}

const WHY = {
  'not-assessed': 'has not been assessed against it',
  'no-assessment': 'has not been assessed against it',
  'assessment-undated': 'has an undated assessment, which does not count',
  'legacy-score-unverified': 'has only an old Evaluations score, which is not a verified assessment',
  'below-required-level': 'was assessed below the required level',
  'level-not-recorded': 'has an assessment with no level recorded',
  'assessed-not-met': 'was assessed as not meeting it',
  'effectiveness-not-assessed': 'has an assessment that did not judge effectiveness',
  'evidence-missing': 'has an assessment without the evidence it requires',
  'criterion-removed':
    'cannot be judged: the criterion it is measured on was removed — an admin must fix the requirement under Settings → Roles',
  'reassessment-due': 'is due a re-assessment: the last one has expired',
};

function buildTask(employee, role, row, employees, at) {
  const req = row.requirement;
  const cycle = cycleOf(row);
  const lapsing = row.status === 'expired' || row.status === 'met';
  const expiresDay = lapsing && row.expiresAt ? String(row.expiresAt).slice(0, 10) : null;
  const due = row.status === 'met' && expiresDay ? expiresDay : new Date(at.getTime() + DUE_DAYS * DAY_MS).toISOString().slice(0, 10);
  const who = employeeLabel(employee);
  const roleName = role.name || role.title || role.id;
  const what = req.description || req.kind || req.id;
  return {
    id: gapTaskId(employee.id, role.id, req.id, cycle),
    title:
      lapsing
        ? `Re-assess competence: ${who} — ${what} (${roleName})`
        : `Close competence gap: ${who} — ${what} (${roleName})`,
    description:
      `${who} holds the role "${roleName}", which requires: ${what}. ` +
      (row.status === 'met'
        ? `Their current assessment lapses on ${expiresDay}`
        : `They ${WHY[row.reason] || WHY[row.status] || 'do not meet it'}` + (expiresDay ? ` (expired ${expiresDay})` : '')) +
      '. Record a fresh assessment with evidence on their Competence section (Employees → the employee → Compliance → Competence). ' +
      'This task closes itself when an assessment recorded after it was created shows the requirement met; it cannot be closed by hand.',
    status: 'open',
    // Null = every tenant admin sees it in My action items
    // (UNASSIGNED_ADMIN_TASK_TYPES) — an action always has an owner (§12).
    priority: row.status === 'expired' ? 'high' : 'medium',
    assigneeId: lineManagerUserId(employee, employees) || null,
    dueDate: due,
    createdAt: at.toISOString(),
    createdBy: 'system',
    type: TASK_TYPE,
    source: 'engine',
    sourceType: SOURCE_TYPE,
    sourceId: `${employee.id}:${role.id}:${req.id}`,
    linkedEmployeeId: employee.id,
    linkedRoleId: role.id,
    linkedRequirementId: req.id,
    gapStatus: row.status,
    gapReason: row.reason || row.status,
    expiresAt: lapsing ? row.expiresAt || null : null,
    cycle,
  };
}

function isOpen(task) {
  return task && task.status !== 'closed';
}

function closeGapTask(task, reason, at, extra = {}) {
  return withSystemClosure(
    { ...task, status: 'closed', closedAt: at.toISOString(), closedBy: 'system', closedReason: reason, ...extra },
    { trigger: 'role-competence', at }
  );
}

/**
 * The assessment that may close `task`: the requirement is Met NOW on a
 * recorded assessment (never training, never a legacy score) dated after the
 * task was created. Null otherwise.
 */
function closingAssessment(task, row, atMs) {
  if (!row || row.status !== 'met' || isOwed(row, atMs)) return null;
  const created = toMs(task.createdAt);
  if (!Number.isFinite(created)) return null;
  // A training requirement whose role says completion suffices (Agreed
  // design 5) closes on a completion dated after the task (co-owner Q2).
  if (row.source === 'training') {
    const done = toMs(row.trainingCompletedAt);
    return Number.isFinite(done) && done > created ? { id: null, kind: 'training', at: row.trainingCompletedAt } : null;
  }
  if (row.source !== 'assessment' || !row.assessment || !hasEvidence(row.assessment.evidence)) return null;
  const assessed = toMs(row.assessment.assessedAt);
  if (!Number.isFinite(assessed) || assessed <= created) return null;
  return row.assessment;
}

/**
 * Reconcile competence-gap tasks against the employees, roles and assessments.
 *
 * @param {object}   input
 * @param {object[]} input.tasks        state.adhocTasks
 * @param {object[]} input.employees    state.employees
 * @param {object[]} input.roles        state.roleDefinitions
 * @param {object}   input.context      { criteria, trainingModules, trainingPrograms, evidenceRecords }
 * @param {string[]} [input.onlyEmployeeIds]  reconcile only these employees
 * @param {boolean}  [input.allowCreate=true]  false ⇒ open no new task (RFLG-B-007:
 *   the roleCompetence flag is not released). Existing tasks are still closed and
 *   reopened; an open task whose cycle moved is NOT superseded, because its
 *   successor could not be created — it stays open and keeps the gap visible.
 * @param {Date}     input.at
 * @returns {{ tasks: object[], changed: boolean, events: object[] }}
 *   events: { kind: 'created'|'closed'|'reopened', task, reason? }
 */
function planCompetenceGapTasks(input) {
  const at = input.at instanceof Date ? input.at : new Date(input.at);
  const tasks = Array.isArray(input.tasks) ? input.tasks : [];
  const employees = Array.isArray(input.employees) ? input.employees : [];
  const roles = (Array.isArray(input.roles) ? input.roles : []).filter((r) => r && r.id);
  const ctx = input.context || {};
  const only = Array.isArray(input.onlyEmployeeIds) ? new Set(input.onlyEmployeeIds) : null;
  const inScope = (id) => !only || only.has(id);
  const allowCreate = input.allowCreate !== false;

  const next = [...tasks];
  const indexOf = new Map(next.map((t, i) => [t && t.id, i]));
  const events = [];
  const replace = (i, task, kind, reason) => {
    next[i] = task;
    events.push({ kind, task, reason });
  };

  // Every requirement each in-scope active employee currently owes, by key.
  const owed = new Map(); // `${emp}|${role}|${req}` → { employee, role, row }
  const empById = new Map();
  for (const employee of employees) if (employee && employee.id) empById.set(employee.id, employee);
  // Sample people are never owed a task (GH #7870).
  for (const employee of realEmployees(employees)) {
    if (!employee || !employee.id || !inScope(employee.id) || !isActiveEmployee(employee)) continue;
    for (const role of heldRoles(employee, roles)) {
      const { requirements } = deriveCompetenceStatus(role, employee, ctx, at);
      for (const row of requirements) {
        if (!row.requirement || !row.requirement.id) continue;
        owed.set(`${employee.id}|${role.id}|${row.requirement.id}`, { employee, role, row });
      }
    }
  }

  // Existing tasks: close, supersede or reopen.
  for (let i = 0; i < next.length; i++) {
    const t = next[i];
    if (!t || t.type !== TASK_TYPE || !inScope(t.linkedEmployeeId)) continue;
    const key = `${t.linkedEmployeeId}|${t.linkedRoleId}|${t.linkedRequirementId}`;
    const hit = owed.get(key);
    if (!hit) {
      if (!isOpen(t)) continue;
      const emp = empById.get(t.linkedEmployeeId);
      let reason = 'requirement-removed';
      if (!emp) reason = 'employee-removed';
      else if (!isActiveEmployee(emp)) reason = 'employee-departed';
      else if (!heldRoles(emp, roles).some((r) => r.id === t.linkedRoleId)) reason = 'role-unassigned';
      replace(i, closeGapTask(t, reason, at), 'closed', reason);
      continue;
    }
    const { row } = hit;
    const wantedCycle = isOwed(row, at.getTime()) ? cycleOf(row) : null;
    if (isOpen(t)) {
      const a = closingAssessment(t, row, at.getTime());
      if (a) {
        const why = a.kind === 'training' ? 'training-completed' : 'assessed-met';
        replace(i, closeGapTask(t, why, at, { closedByAssessmentId: a.id || null }), 'closed', why);
      } else if (allowCreate && wantedCycle && t.cycle && t.cycle !== wantedCycle) {
        const supersededBy = gapTaskId(t.linkedEmployeeId, t.linkedRoleId, t.linkedRequirementId, wantedCycle);
        replace(i, closeGapTask(t, 'superseded', at, { supersededBy }), 'closed', 'superseded');
      }
      continue;
    }
    // Closed: reopen only when its own cycle is unmet again (an assessment
    // that closed it was followed by a worse one, or it was closed because
    // the role was unassigned and is held again).
    if (wantedCycle && (t.cycle || 'open') === wantedCycle) {
      const {
        closedAt: _a,
        closedBy: _b,
        closedReason: _c,
        closedByAssessmentId: _d,
        supersededBy: _e,
        ...rest
      } = t;
      const reopenReason = t.closedReason === 'assessed-met' ? 'unmet-again' : 'owed-again';
      const reopenings = [...(Array.isArray(t.reopenings) ? t.reopenings : []), { at: at.toISOString(), reason: reopenReason, after: t.closedReason || null }];
      replace(
        i,
        { ...rest, reopenings, status: 'open', gapStatus: row.status, gapReason: row.reason || row.status, reopenedAt: at.toISOString(), reopenReason },
        'reopened',
        reopenReason
      );
    }
  }

  // Missing tasks: one per unmet requirement and cycle. None while creation is
  // gated off; nothing is queued for later — a tenant has no backlog on release.
  for (const { employee, role, row } of allowCreate ? owed.values() : []) {
    if (!isOwed(row, at.getTime())) continue;
    const id = gapTaskId(employee.id, role.id, row.requirement.id, cycleOf(row));
    if (indexOf.has(id)) continue;
    const task = buildTask(employee, role, row, employees, at);
    indexOf.set(id, next.length);
    next.push(task);
    events.push({ kind: 'created', task });
  }

  return { tasks: next, changed: events.length > 0, events };
}

/**
 * What stops while the roleCompetence flag is OFF (RFLG-B-007), for the
 * release cutover preview (the roleCompetence registry entry's haltedProbe):
 *   count               tasks the reconcile would open now and cannot while
 *                       OFF — the planner's own creations, so it can never
 *                       disagree with what the reconcile does
 *   soonestOverdueDays  whole days until the earliest assessment expiry among
 *                       requirements active employees hold (negative when one
 *                       has already lapsed); null when none expires
 *   openRuns            open gap tasks, which are still closed and reopened
 * Sample people are never counted (GH #7870).
 */
function haltedCompetenceGapWork(input) {
  const at = input.at instanceof Date ? input.at : new Date(input.at);
  const employees = Array.isArray(input.employees) ? input.employees : [];
  const roles = (Array.isArray(input.roles) ? input.roles : []).filter((r) => r && r.id);
  const tasks = Array.isArray(input.tasks) ? input.tasks : [];
  const { events } = planCompetenceGapTasks({ ...input, onlyEmployeeIds: undefined, allowCreate: true, at });
  let soonest = null;
  for (const employee of realEmployees(employees)) {
    if (!employee || !employee.id || !isActiveEmployee(employee)) continue;
    for (const role of heldRoles(employee, roles)) {
      for (const row of deriveCompetenceStatus(role, employee, input.context || {}, at).requirements) {
        const exp = toMs(row.expiresAt);
        if (!row.requirement || !Number.isFinite(exp)) continue;
        const days = Math.ceil((exp - at.getTime()) / DAY_MS);
        if (soonest === null || days < soonest) soonest = days;
      }
    }
  }
  return {
    count: events.filter((e) => e.kind === 'created').length,
    soonestOverdueDays: soonest,
    openRuns: tasks.filter((t) => t && t.type === TASK_TYPE && isOpen(t)).length,
  };
}

/**
 * state-write's guard for competence-gap tasks (RCQA-04). Only the server
 * creates, closes, deletes or relinks one; a person works it (status open ⇄
 * in-progress, assignee, due date, notes) and closes it by recording the
 * assessment. Restore, don't reject — the rest of the save lands.
 *
 *   - a competence-gap task the payload closes, or that a stored-closed task
 *     arrives reopened → the stored task comes back.
 *   - a stored competence-gap task missing from the payload → put back.
 *   - a new competence-gap task (not stored) → dropped.
 *   - server-owned fields → the stored values.
 *
 * Runs AFTER guardAdhocTaskClosures, on its output.
 *
 * @returns {{ tasks: object[], restored: { taskId: string, rule: string }[] }}
 */
function guardCompetenceGapTasks(incoming, stored) {
  if (!Array.isArray(incoming)) return { tasks: incoming, restored: [] };
  const priorList = (Array.isArray(stored) ? stored : []).filter((t) => t && t.id && t.type === TASK_TYPE);
  const priorById = new Map(priorList.map((t) => [t.id, t]));
  const restored = [];
  const seen = new Set();
  const tasks = [];
  for (const t of incoming) {
    if (!t || typeof t !== 'object' || !t.id) {
      tasks.push(t);
      continue;
    }
    const prior = priorById.get(t.id) || null;
    if (!prior) {
      const storedOther = (Array.isArray(stored) ? stored : []).find((s) => s && s.id === t.id);
      if (storedOther && (t.type === TASK_TYPE || t.sourceType === SOURCE_TYPE)) {
        restored.push({ taskId: t.id, rule: 'competence-gap-retyped-by-client' });
        tasks.push(storedOther);
        continue;
      }
      if (t.type === TASK_TYPE || t.sourceType === SOURCE_TYPE) {
        restored.push({ taskId: t.id, rule: 'competence-gap-created-by-client' });
        continue;
      }
      tasks.push(t);
      continue;
    }
    seen.add(t.id);
    const priorClosed = prior.status === 'closed';
    const nowClosed = t.status === 'closed';
    if (priorClosed !== nowClosed) {
      restored.push({ taskId: t.id, rule: nowClosed ? 'competence-gap-closed-by-hand' : 'competence-gap-reopened-by-hand' });
      tasks.push(prior);
      continue;
    }
    let task = t;
    const drift = SERVER_OWNED_FIELDS.filter((f) => JSON.stringify(t[f]) !== JSON.stringify(prior[f]));
    if (drift.length) {
      task = { ...t };
      for (const f of SERVER_OWNED_FIELDS) {
        if (f in prior) task[f] = prior[f];
        else delete task[f];
      }
      restored.push({ taskId: t.id, rule: 'competence-gap-server-owned-fields' });
    }
    tasks.push(task);
  }
  for (const prior of priorList) {
    if (seen.has(prior.id)) continue;
    restored.push({ taskId: prior.id, rule: 'competence-gap-deleted-by-client' });
    tasks.push(prior);
  }
  return { tasks, restored };
}

module.exports = {
  TASK_TYPE,
  SOURCE_TYPE,
  DUE_DAYS,
  LEAD_DAYS,
  isOwed,
  SERVER_OWNED_FIELDS,
  gapTaskId,
  heldRoles,
  closingAssessment,
  buildTask,
  planCompetenceGapTasks,
  haltedCompetenceGapWork,
  guardCompetenceGapTasks,
};
